import React, { useState, useEffect } from 'react';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import SettingsModal from './SettingsModal';

const getNameInitials = (name: string | undefined | null): string => {
  if (!name) return '';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

interface ProfileMenuProps {
  user?: {
    name?: string;
    email?: string;
  };
}

export default function ProfileMenu({ user: initialUser }: ProfileMenuProps) {
  const { user } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    // Initialize from localStorage first (following Telemore pattern)
    try {
      const storedUser = localStorage.getItem('fmla_doctor_user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        // Defensive check - prefer stored data if it's more complete
        if (parsed && parsed.name && parsed.name !== 'User') {
          return parsed;
        }
      }
    } catch (error) {
    }
    return initialUser || user;
  });

  // Listen for user data updates from auth context with defensive handling
  useEffect(() => {
    if (user) {
      // Only update if new user data is more complete than current
      if (!currentUser || 
          !currentUser.name || 
          currentUser.name === 'User' ||
          (user.name && user.name !== 'User' && user.name !== currentUser.name)) {
        setCurrentUser(user);
      }
    }
  }, [user, currentUser]);

  // Listen for custom events for real-time synchronization (Telemore pattern)
  useEffect(() => {
    const handleUserDataUpdated = (event: CustomEvent) => {
      if (event.detail && event.detail.user) {
        const newUser = event.detail.user;
        // Defensive update - only if new data is more complete
        if (!currentUser || 
            !currentUser.name || 
            currentUser.name === 'User' ||
            (newUser.name && newUser.name !== 'User' && newUser.name !== currentUser.name)) {
          setCurrentUser(newUser);
        }
      }
    };

    // Add event listener for custom event
    window.addEventListener('userDataUpdated', handleUserDataUpdated as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdated as EventListener);
    };
  }, [currentUser]);

  const handleClick = () => {
    setSettingsOpen(true);
  };

  return (
    <React.Fragment>
      {/* Profile Menu */}
      <Stack
        gap={1}
        direction="row"
        alignItems="center"
        onClick={handleClick}
        sx={{ cursor: 'pointer' }}
      >
        {/* Avatar comes first */}
        <Box>
          <IconButton size="small" sx={{ '&:focus': { outline: 0 } }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'primary.main' }}>
              {getNameInitials(currentUser?.name)}
            </Avatar>
          </IconButton>
        </Box>

        {/* Hidden at less than medium screen */}
        <Box textAlign="left" display={{ xs: 'none', sm: 'block' }}>
          <Typography
            variant="body2"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              color: '#333',
            }}
          >
            {currentUser?.name || 'User'}
          </Typography>
        </Box>
      </Stack>

      {/* Settings Modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </React.Fragment>
  );
}